import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Dr. Jason Rivers — dermatology authority / founder story. Deep sage
// canvas, asymmetric editorial composition (portrait ≈58%, content
// ≈42%) with a subtle portrait parallax and sequential reveals. Every
// fact is verified from riversol.com/pages/about-us:
//   • "Award-winning" + "Board-certified dermatologist"
//   • "more than 20 years of clinical dermatology"
//   • "President of both the Canadian Dermatology Association and the
//      Acne and Rosacea Society of Canada"
//   • "decades publishing" + verified ORCID / Google Scholar / PubMed
//   • Created Riversol; "Each formula is reviewed and approved by Dr. Rivers"
const CREDENTIALS = [
  {
    label: "Dermatologist",
    detail: "Board-certified, 20+ years of clinical practice in Vancouver.",
  },
  {
    label: "Research",
    detail: "Decades publishing — profiles on ORCID, Google Scholar & PubMed.",
  },
  {
    label: "Founder",
    detail: "Created Riversol and reviews & approves every formula.",
  },
];

export const DrRiversStory = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
  // Scroll-linked headline reveal: the second phrase warms from muted to
  // full white as the section passes through the viewport.
  const phraseOpacity = useTransform(scrollYProgress, [0.18, 0.5], [0.45, 1]);

  return (
    <section
      ref={ref}
      className="relative bg-riversol-blue text-white py-20 lg:py-28 overflow-hidden"
      data-testid="home-dr-rivers-story"
    >
      {/* subtle grain / ambient texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center">
        {/* Portrait — larger side of the asymmetric split */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6"
        >
          <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden bg-white/5 rounded-sm">
            <motion.img
              src="https://www.riversol.com/cdn/shop/files/img-jr_40_ba4b085e-5569-45ab-867c-3cd3d9c0f22c.png?width=1400"
              alt="Dr. Jason Rivers"
              className="absolute inset-0 w-full h-[110%] object-cover"
              style={{ y }}
              initial={{ scale: 1.06 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 8, ease: "linear" }}
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-riversol-blue/60 via-transparent to-transparent pointer-events-none" />
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="absolute left-6 bottom-6 z-10 font-mono text-[10px] tracking-[0.22em] uppercase text-white/85"
            >
              Dr. Jason Rivers · Founder
            </motion.div>
          </div>
        </motion.div>

        {/* Narrative — smaller side */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="hidden font-mono text-[10px] tracking-[0.28em] uppercase text-white/50"
          >
            Dermatology, first
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 26 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 text-[38px] sm:text-[42px] lg:text-[56px] leading-[1.04] tracking-[-0.02em]"
          >
            Dermatology, first.{" "}
            <motion.span className="italic" style={{ opacity: phraseOpacity }}>
              Everything else follows.
            </motion.span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.75, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-[16px] leading-[1.65] text-white/75"
          >
            Award-winning, board-certified dermatologist Dr. Jason Rivers created
            Riversol after more than 20 years of clinical practice — treating
            patients who were using too many products with too little guidance.
            After seven years of development he formulated the first Riversol
            serum for his rosacea patients. He still sees patients in Vancouver,
            and reviews and approves every Riversol formula.
          </motion.p>

          {/* Credential details — deliberately not a number rail */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-6">
            {CREDENTIALS.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 18 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.42 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="pt-4 border-t border-white/20"
                data-testid={`credential-${i}`}
              >
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50">
                  {c.label}
                </div>
                <div className="mt-2 text-[13px] leading-relaxed text-white/80">
                  {c.detail}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.a
            href="https://www.riversol.com/pages/about-us"
            data-testid="dr-rivers-cta"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-11 inline-flex items-center gap-2 text-[13px] tracking-[0.14em] uppercase font-medium text-white border-b border-white/30 pb-2 hover:border-white transition-colors"
          >
            Meet Dr. Rivers
            <ArrowRight size={16} strokeWidth={1.8} />
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default DrRiversStory;
